import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import axios from 'axios'
import { storage } from '@/lib/storage'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useSignInWithUserAndPassword } from '@/modules/auth/service/auth'
import { Loader } from '@/components/common/Loader'
import { BrandLogo } from '@/components/common/BrandLogo'
import { Footer } from '@/components'

export const Route = createFileRoute('/auth/login')({
  component: LoginPage,
  beforeLoad: async () => {
    const accessToken = storage.get('accessToken')
    const refreshToken = storage.get('refreshToken')
    if (accessToken && refreshToken) {
      throw new Error('REDIRECT_TO_ADMIN')
    }
  },
  errorComponent: ({ error }) => {
    if (error instanceof Error && error.message === 'REDIRECT_TO_ADMIN') {
      window.location.href = '/admin'
      return null
    }
    return <div>An error occurred</div>
  },
})

function LoginPage() {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const navigate = useNavigate()

  const $apiLogin = useSignInWithUserAndPassword()
  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    try {
      const response = await $apiLogin.mutateAsync({
        username: username,
        password,
      })

      const { accessToken, refreshToken } = response.data

      console.log('Login response:', response.data)

      if (response.data.user.status === 'inactive') {
        setError('บัญชีผู้ใช้งานถูกปิดใช้งาน โปรดติดต่อผู้ดูแลระบบ')
        return
      }
      storage.set('accessToken', accessToken)
      storage.set('refreshToken', refreshToken)
      // Store role key if role is an object, otherwise store the value directly
      if (response.data.user?.role) {
        const roleValue = typeof response.data.user.role === 'object' 
          ? response.data.user.role.key || response.data.user.role.name 
          : response.data.user.role
        if (roleValue) {
          storage.set('userRole', roleValue)
        }
      }
      // Use window.location to force a full page reload and trigger beforeLoad
      window.location.href = '/admin'
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 401) {
          setError('ชื่อผู้ใช้งานหรือรหัสผ่านไม่ถูกต้อง')
          return
        }
        if (error.response?.status === 403) {
          setError(error.response.data.message || 'คุณไม่มีสิทธิ์เข้าถึงระบบนี้')
          return
        }
      }
      setError(error instanceof Error ? error.message : 'An error occurred')
    } finally {
      setLoading(false)
    }
  }

  // OAuth login removed - Supabase is no longer used
  // const handleOAuthLogin = async (provider: 'github' | 'google') => {
  //   setLoading(true)
  //   setError('')
  //   setLoading(false)
  // }

  if (loading) {
    return <Loader.FullScreen />
  }

  return (
    <div className="login-page relative flex items-center justify-center min-h-screen">
      <div className="w-full max-w-md p-8 space-y-8 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg z-10">
        <div className="text-center space-y-4">
          <div className="flex justify-center">
            <BrandLogo className="mx-auto h-32 w-auto max-w-full sm:h-40" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">ยินดีต้อนรับสู่ระบบ</h1>
            <p className="text-gray-500">เข้าสู่ระบบเพื่อใช้งานระบบ</p>
          </div>
        </div>

        {error && (
          <div className="p-4 text-sm text-red-600 bg-red-50 rounded-md">{error}</div>
        )}

        <form className="space-y-6" onSubmit={handleEmailLogin}>
          <div className="space-y-2">
            <Label htmlFor="username">ชื่อผู้ใช้งาน</Label>
            <Input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              placeholder="ชื่อผู้ใช้งาน"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">รหัสผ่าน</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="รหัสผ่าน"
            />
          </div>

          <Button className="w-full" type="submit" disabled={loading}>
            {loading ? 'กำลังเข้าสู่ระบบ...' : 'เข้าสู่ระบบ'}
          </Button>
        </form>
        <Footer />
      </div>
      <div className="wave"></div>
      <div className="wave"></div>
      <div className="wave"></div>
    </div>
  )
}

